// Step 3 — Pick your handle.
//
// Local format validation as you type; a GitHub availability check
// (both target repos must be free) on Continue. Shows the repo names
// and default site URL the handle will produce.

import { useState } from "react";
import { useAuthStore } from "../../store/authStore";
import { useOnboardingStore } from "../../store/onboardingStore";
import { handleError, isValidHandle, privateRepoName, publicRepoName, defaultSiteUrl } from "../../lib/handle";

export function StepHandle() {
  const user = useAuthStore((s) => s.user)!;
  const githubLogin = useOnboardingStore((s) => s.githubIdentity?.login) ?? "you";
  const busy = useOnboardingStore((s) => s.busy);
  const error = useOnboardingStore((s) => s.error);
  const checkHandle = useOnboardingStore((s) => s.checkHandle);
  const chooseHandle = useOnboardingStore((s) => s.chooseHandle);
  const clearError = useOnboardingStore((s) => s.clearError);

  const [handle, setHandle] = useState("");
  const [takenReason, setTakenReason] = useState<string | null>(null);

  const localErr = handleError(handle);
  const valid = isValidHandle(handle);

  const onChange = (v: string) => {
    setHandle(v.toLowerCase());
    setTakenReason(null);
    if (error) clearError();
  };

  const onContinue = async () => {
    if (!valid || busy) return;
    const avail = await checkHandle(user.sub, handle);
    if (!avail.ok) {
      setTakenReason(avail.reason ?? "That handle isn't available.");
      return;
    }
    await chooseHandle(user.sub, handle);
  };

  return (
    <div className="step">
      <div className="step-kicker">Step 3 of 7</div>
      <h1 className="step-title">Pick your handle</h1>
      <p className="step-why">
        A short slug that names your repos and your site's web address. Lowercase letters,
        digits, and hyphens. You can't change it later without re-running setup, so pick
        something you'll be happy seeing in your URL.
      </p>

      <div className="step-field">
        <label className="step-label" htmlFor="handle">Your handle</label>
        <input
          id="handle"
          className="step-input"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="chris"
          value={handle}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void onContinue(); }}
        />
        {localErr && <div className="step-hint" style={{ color: "var(--rust)" }}>{localErr}</div>}
      </div>

      {valid && (
        <div className="handle-preview">
          <div className="handle-preview-row"><span>private repo</span><code>{githubLogin}/{privateRepoName(handle)}</code></div>
          <div className="handle-preview-row"><span>public repo</span><code>{githubLogin}/{publicRepoName(handle)}</code></div>
          <div className="handle-preview-row"><span>your site</span><code>{defaultSiteUrl(handle).replace("https://", "")}</code></div>
        </div>
      )}

      <div className="step-actions">
        <button className="step-btn" disabled={!valid || busy} onClick={() => void onContinue()}>
          {busy ? "Checking…" : "Continue"}
        </button>
      </div>

      {takenReason && (
        <div className="step-error">
          <div className="step-error-title">handle unavailable</div>
          {takenReason}
        </div>
      )}
      {error && !takenReason && (
        <div className="step-error">
          <div className="step-error-title">something went wrong</div>
          {error}
        </div>
      )}

      <style>{`
        .handle-preview {
          background: var(--paper-raised);
          border: 1px solid var(--line);
          border-radius: var(--radius-1);
          padding: var(--space-3) var(--space-4);
          margin-bottom: var(--space-2);
        }
        .handle-preview-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: var(--space-4); padding: 5px 0;
        }
        .handle-preview-row span {
          font-family: var(--font-mono); font-size: 11px;
          text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-faint);
        }
        .handle-preview-row code {
          font-family: var(--font-mono); font-size: 13px; color: var(--ink);
        }
      `}</style>
    </div>
  );
}
